import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import config from 'config';
import { Button } from 'components/ui/button';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import { GetWallet } from 'modules/coins/api';
import { formatCoins } from 'modules/coins/constants';

const WALLET_QUERY_KEY = ['coins', 'wallet'];

/** Shares the `['coins','wallet']` cache, so it costs no extra request. Clickable only with BILLING_EDIT. */
export function HeaderCoins() {
  const { t } = useTranslation();
  const { open: openSettings } = useSettingsDialog();
  const { state } = useAuthContext();
  const canView = usePermission(PERMISSIONS.BILLING_VIEW);
  const canEdit = usePermission(PERMISSIONS.BILLING_EDIT);
  const queryClient = useQueryClient();

  // Read once per org change rather than on every render. Re-derived when
  // state.organization?.id changes — the org switcher updates that state
  // before it persists the new id to localStorage, so this stays in sync.
  const orgId = useMemo(
    () => localStorage.getItem(config.api.active_org_key) || state.organization?.id,
    [state.organization?.id],
  );
  const enabled = canView && state.isAuthenticated && Boolean(orgId);

  // The wallet query key is intentionally NOT scoped by orgId — several other
  // screens (billing page, RewardsPanel, CheckoutModal, AiAssistCard) read the
  // exact same ['coins','wallet'] key so they all share one cache entry and
  // one network request. Forking this component's key to include orgId would
  // desync it from those consumers (extra request here, and THEY would still
  // show the stale org's balance). Instead, invalidate the shared key when the
  // active org changes so every mounted consumer refetches the new org's data.
  const prevOrgId = useRef(orgId);
  useEffect(() => {
    if (prevOrgId.current === orgId) return;
    prevOrgId.current = orgId;
    queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEY });
  }, [orgId, queryClient]);

  const { data: wallet } = useQuery({
    queryKey: WALLET_QUERY_KEY,
    queryFn: async () => (await GetWallet()).data,
    enabled,
    staleTime: 30_000,
  });

  if (!enabled || !wallet) return null;

  const label = formatCoins(wallet.balance);
  // The sidebar footer row: "Balans" on the left, the figure in ink on the right.
  const content = (
    <>
      <Coins className="h-4 w-4 shrink-0" aria-hidden />
      <span className="flex-1 text-left">{t('portal.balance')}</span>
      <span className="text-xs font-semibold tabular-nums text-foreground">{label}</span>
    </>
  );

  if (!canEdit) {
    return (
      <span
        className="flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] text-muted-foreground"
        aria-label={t('coins_balance_aria', 'Your coin balance')}
        title={label}
      >
        {content}
      </span>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => openSettings('billing')}
      aria-label={t('coins_balance_aria', 'Your coin balance')}
      title={label}
      className="flex h-9 w-full items-center justify-start gap-2.5 px-2.5 text-[13px] font-normal text-muted-foreground hover:text-foreground"
    >
      {content}
    </Button>
  );
}

export default HeaderCoins;
